import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { fetchAudioLibrary } from "@/lib/api";
import { useAudioPlayer } from "@/context/AudioPlayerContext";
import { ytId } from "@/components/AudioMedia";
import { Music, Radio, Play, AudioLines, Youtube, ArrowUpRight } from "lucide-react";

const accentFor = (s = "") => ["#C75000", "#B8860B", "#2E7D32", "#1F5C9C", "#7E57C2", "#B3241C"][[...s].reduce((a, c) => a + c.charCodeAt(0), 0) % 6];

// Homepage "Listen & Live" band — curated audio + live radio channels.
// Content is admin-controlled: featured tracks via show_on_home, channels via Admin → Audio Library.
export default function HomeAudioLive() {
  const { pick } = useLang();
  const { play, playChannel, track, channel: liveCh } = useAudioPlayer();
  const [data, setData] = useState(null);
  useEffect(() => { fetchAudioLibrary().then(setData).catch(() => setData({ sections: [], channels: [] })); }, []);

  const tracks = (data?.sections || []).flatMap((s) => s.tracks || []);
  const featured = tracks.filter((t) => t.show_on_home);
  const picks = (featured.length ? featured : tracks).slice(0, 4);
  const channels = (data?.channels || []).filter((c) => (c.tracks || []).length);
  if (!picks.length && !channels.length) return null;

  return (
    <section className="mx-auto max-w-6xl px-5 py-14 md:px-8 md:py-20" data-testid="home-audio-live">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="overline-label flex items-center gap-2 text-saffron"><Music className="h-3.5 w-3.5" /> {pick("सुनें एवं लाइव", "Listen & Live")}</p>
          <h2 className="mt-2 font-serif-latin text-2xl text-ink md:text-3xl">{pick("भक्ति संगीत एवं लाइव चैनल", "Devotional audio & live channels")}</h2>
        </div>
        <Link to="/audios" data-testid="home-audio-all" className="inline-flex shrink-0 items-center gap-1.5 border border-saffron px-4 py-2 text-sm font-medium text-saffron transition-colors hover:bg-[var(--saffron)] hover:text-white">
          {pick("ध्वनि संग्रह", "Audio Library")} <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>

      {channels.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-3" data-testid="home-live-channels">
          {channels.map((c) => {
            const active = liveCh && liveCh.title === c.title;
            const color = c.color || accentFor(c.title);
            return (
              <button key={c.id} data-testid={`home-live-${c.id}`} onClick={() => playChannel(c)}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium text-white transition-transform hover:-translate-y-0.5 ${active ? "ring-2 ring-[var(--gold)] ring-offset-2 ring-offset-[var(--bg)]" : ""}`}
                style={{ background: `linear-gradient(135deg, ${color}, ${color}bb)` }}>
                <Radio className="h-3.5 w-3.5" /> {pick(c.title_hi || c.title, c.title)} <span className="rounded bg-white/25 px-1.5 text-[10px] uppercase">{active ? pick("चालू", "live") : c.lang}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {picks.map((a) => {
          const title = pick(a.title_hi || a.title, a.title);
          const yid = a.kind === "youtube" ? ytId(a.url) : "";
          const thumb = a.image || (yid ? `https://img.youtube.com/vi/${yid}/hqdefault.jpg` : "");
          const accent = accentFor(a.category || a.title);
          const active = track && track.id === a.id;
          return (
            <button key={a.id} data-testid={`home-audio-${a.id}`} onClick={() => play({ ...a, title })}
              className={`group relative flex flex-col overflow-hidden rounded-xl border bg-surface text-left transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${active ? "border-[var(--gold)] ring-2 ring-[var(--gold)]/40" : "border-line hover:border-[var(--gold)]"}`}>
              <div className="relative aspect-square overflow-hidden">
                {thumb ? <img src={thumb} alt={title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  : <div className="flex h-full w-full items-center justify-center" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}aa)` }}><AudioLines className="h-10 w-10 text-white/70" /></div>}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <span className="absolute bottom-2 right-2 flex h-10 w-10 items-center justify-center rounded-full bg-[var(--saffron)] text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100"><Play className="h-4 w-4" fill="currentColor" /></span>
              </div>
              <div className="p-3">
                <p className="truncate font-serif-latin text-sm text-ink">{title}</p>
                <p className="mt-0.5 flex items-center gap-1 text-[11px] text-ink-3">{a.kind === "youtube" ? <Youtube className="h-3 w-3" /> : <AudioLines className="h-3 w-3" />} {a.deity || a.category || "Audio"}</p>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}
